import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./ManagerDashboard.css";
import { useDispatch, useSelector } from 'react-redux';
import { selectAdminToken } from "../../admin/sessionSlice";
import { getManagerSettings } from "../../functions/getManagerSettings";
import { addManagerQuestions } from "../../functions/addManagerQuestions";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { getLobbyGameData } from "../../functions/getLobbyGameData";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import { setBackButtonUrl } from '../uiSlice';


// ── Extramile URLs from environment ──────────────────────────────────────────
const EXTRAMILE_GUEST_JOIN = process.env.REACT_APP_EXTRAMILE_GUEST_JOIN_URL || "";
const EXTRAMILE_SIGNUP = process.env.REACT_APP_EXTRAMILE_SIGNUP_URL || "";

function buildExtramileJoinUrl(isGuest, gameId, sessionId, organizationId) {
  const rawBase = isGuest ? EXTRAMILE_GUEST_JOIN : EXTRAMILE_SIGNUP;
  const base = String(rawBase || "").replace(/\/$/, "");
  if (!base) return "";
  const u = new URL(base);
  u.searchParams.set("gameId", String(gameId ?? ""));
  u.searchParams.set("sessionId", String(sessionId ?? ""));
  u.searchParams.set("organizationId", String(organizationId ?? ""));
  return u.toString();
}

function appendLobbyShareFlagsToJoinUrl(baseUrl, lobbyId) {
  const base = String(baseUrl ?? "").trim();
  const id = String(lobbyId ?? "").trim();
  if (!base) return "";
  if (!id) return base;
  try {
    const u = new URL(base);
    u.searchParams.set("lobby", id);
    return u.toString();
  } catch {
    return base;
  }
}

function deriveGuestFromUser(u) {
  const raw = String(u?.user_type ?? u?.userType ?? u?.role ?? u?.source ?? "").trim();
  const ut = raw.toUpperCase().replace(/\s+/g, "_").replace(/-+/g, "_");
  if (ut === "ORG_USER" || ut === "ORG" || ut === "ORGUSER" || ut.startsWith("ORG_")) return false;
  if (ut === "GUEST_USER" || ut === "GUEST" || ut.startsWith("GUEST_")) return true;
  return true;
}

function buildFallbackAppLink(lobbyName) {
  const base = String(process.env.REACT_APP_BASE_URL || window.location.origin || "").replace(/\/+$/, "");
  const l = encodeURIComponent(String(lobbyName || ""));
  if (!base) return "";
  if (!l) return `${base}/login`;
  return `${base}/login?lobby=${l}`;
}

function withQuestionAmp(url) {
  const s = String(url || "");
  return s.includes("?") ? s.replace("?", "?&") : s;
}

// ── Responsive helper ─────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 700);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: (isMobile) => ({
    minHeight: "90vh",
    fontFamily: "'Nunito', sans-serif",
    padding: isMobile ? "20px 14px 80px" : "36px 24px 80px",
    position: "relative",
    overflow: "hidden",
  }),
  bgBlob1: {
    position: "fixed",
    top: -60,
    left: -60,
    width: 220,
    height: 220,
    background: "rgba(99,162,255,0.18)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  },
  bgBlob2: {
    position: "fixed",
    bottom: 40,
    left: 20,
    width: 160,
    height: 160,
    background: "rgba(150,130,255,0.13)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  },
  qmarks: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: 80,
    display: "flex",
    flexDirection: "column",
    gap: 60,
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "none",
    zIndex: 0,
  },
  qmark: { fontSize: 38, fontWeight: 900, color: "rgba(120,140,210,0.12)" },
  inner: { maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 1 },
  title: (isMobile) => ({
    textAlign: "center",
    fontSize: isMobile ? 26 : 44,
    fontWeight: 900,
    color: "white",
    marginBottom: isMobile ? 18 : 30,
    letterSpacing: -1,
    textShadow: "0 2px 20px rgba(59,130,246,0.15)",
    fontFamily: "'Nunito', sans-serif",
  }),
  topRow: (isMobile) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? 14 : 20,
    marginBottom: isMobile ? 18 : 28,
  }),
  card: (isMobile) => ({
    background: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(18px)",
    border: "1.5px solid rgba(255,255,255,0.9)",
    borderRadius: isMobile ? 16 : 20,
    padding: isMobile ? "18px 16px" : "24px 28px",
    boxShadow: "0 6px 28px rgba(100,120,200,0.09)",
  }),
  cardTitle: (isMobile) => ({
    fontSize: isMobile ? 16 : 20,
    fontWeight: 800,
    color: "#1a2b5e",
    marginBottom: isMobile ? 12 : 16,
  }),
  genCard: (isMobile) => (isMobile ? {} : { flex: "0 0 440px" }),
  settCard: (isMobile) => (isMobile ? {} : { flex: 1 }),
  topicInput: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "11px 16px" : "12px 18px",
    borderRadius: 50,
    border: "1.5px solid #d4dcf8",
    background: "#f4f6fd",
    fontSize: isMobile ? 15 : 15,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 600,
    color: "#1a2b5e",
    outline: "none",
    marginBottom: 12,
    boxSizing: "border-box",
  }),
  genBtn: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "12px 0" : "13px 0",
    borderRadius: 50,
    background: "#920ce1",
    color: "white",
    border: "none",
    fontSize: isMobile ? 15 : 16,
    fontWeight: 800,
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    letterSpacing: 0.5,
    boxShadow: "0 6px 20px rgba(37,99,235,0.35)",
    transition: "opacity 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  }),
  checkRow: { display: "flex", alignItems: "center", gap: 10, cursor: "pointer" },
  checkBox: (checked) => ({
    width: 22,
    height: 22,
    borderRadius: 6,
    background: checked ? "#920ce1" : "#f0f2fa",
    border: `2px solid ${checked ? "#2563eb" : "#c8d0e8"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.15s",
  }),
  checkLabel: { fontSize: 16, fontWeight: 700, color: "#1a2b5e" },
  sectionLabel: (isMobile) => ({
    fontSize: isMobile ? 16 : 20,
    fontWeight: 800,
    color: "white",
    marginBottom: isMobile ? 10 : 14,
  }),
  dropdownCard: (isMobile) => ({
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(18px)",
    border: "1.5px solid rgba(255,255,255,0.9)",
    borderRadius: isMobile ? 14 : 16,
    boxShadow: "0 6px 28px rgba(100,120,200,0.12)",
    marginBottom: 10,
    overflow: "hidden",
  }),
  dropdownHeader: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 10 : 14,
    padding: isMobile ? "12px 14px" : "16px 20px",
    cursor: "pointer",
    userSelect: "none",
  }),
  qBadge: (isMobile) => ({
    width: isMobile ? 36 : 42,
    height: isMobile ? 36 : 42,
    borderRadius: 12,
    background: "linear-gradient(135deg,#dce8fc,#cdd8fa)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isMobile ? 12 : 14,
    fontWeight: 800,
    color: "#2563eb",
    flexShrink: 0,
  }),
  qTitle: (isMobile) => ({ fontWeight: 800, fontSize: isMobile ? 14 : 16, color: "#1a2b5e" }),
  qSub: { fontSize: 13, color: "#6b7db3", fontWeight: 600, marginTop: 2 },
  qActions: { marginLeft: "auto", display: "flex", gap: 8 },
  iconBtn: (color, active = false) => ({
    width: 34,
    height: 34,
    borderRadius: 8,
    border: "none",
    background: active ? "rgba(37,99,235,0.16)" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: active ? "#2563eb" : color,
    fontSize: 16,
    boxShadow: active ? "0 0 0 1px rgba(37,99,235,0.28) inset" : "none",
    transition: "background 0.15s, color 0.15s, box-shadow 0.15s",
  }),
  expandBody: {
    padding: "0 20px 20px",
    borderTop: "1px solid #edf0fa",
    animation: "fadeIn 0.2s ease",
  },
  expandBodyMobile: {
    padding: "0 14px 16px",
    borderTop: "1px solid #edf0fa",
    animation: "fadeIn 0.2s ease",
  },
  optionsGrid: (isMobile) => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: isMobile ? 8 : 10,
    marginTop: 14,
  }),
  optionRow: { display: "flex", alignItems: "center", gap: 8 },
  optLabel: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: "#eef2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    color: "#2563eb",
    flexShrink: 0,
  },
  optInput: {
    flex: 1,
    padding: "9px 14px",
    borderRadius: 10,
    border: "1.5px solid #d4dcf8",
    background: "#f8f9fe",
    fontSize: 14,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 600,
    color: "#1a2b5e",
    outline: "none",
  },
  qInput: (isMobile) => ({
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    border: "1.5px solid #d4dcf8",
    background: "#f8f9fe",
    fontSize: isMobile ? 14 : 14,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 700,
    color: "#1a2b5e",
    outline: "none",
    marginTop: 14,
    boxSizing: "border-box",
  }),
  addNewBtn: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "14px 0" : "18px 0",
    borderRadius: 16,
    border: "2px dashed #b8c8f0",
    background: "rgba(255,255,255,0.5)",
    fontSize: isMobile ? 14 : 16,
    fontWeight: 800,
    color: "#6b7db3",
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    marginBottom: 20,
    transition: "all 0.15s",
  }),
  startBtn: (isMobile) => ({
    width: "100%",
    padding: isMobile ? "16px 0" : "20px 0",
    borderRadius: 50,
    background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
    color: "white",
    border: "none",
    fontSize: isMobile ? 17 : 20,
    fontWeight: 900,
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    letterSpacing: 0.5,
    boxShadow: "0 8px 28px rgba(37,99,235,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  }),
  fab: {
    position: "fixed",
    bottom: 30,
    right: 24,
    width: 54,
    height: 54,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
    color: "white",
    border: "none",
    fontSize: 28,
    fontWeight: 700,
    cursor: "pointer",
    zIndex: 100,
    boxShadow: "0 6px 20px rgba(37,99,235,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  aiDropdown: {
    background: "white",
    border: "1.5px solid #d4dcf8",
    borderRadius: 14,
    boxShadow: "0 8px 28px rgba(37,99,235,0.12)",
    marginTop: 6,
    overflow: "hidden",
    maxHeight: 260,
    overflowY: "auto",
  },
  aiItem: {
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #f0f2fa",
    fontSize: 14,
    fontWeight: 700,
    color: "#1a2b5e",
    transition: "background 0.12s",
  },
  spinner: {
    display: "inline-block",
    width: 16,
    height: 16,
    border: "2.5px solid rgba(255,255,255,0.4)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
};

const OPTION_LABELS = ["A", "B", "C", "D"];

/** Native `title` tooltips on Settings → Lifelines (manager dashboard). */
const LIFELINES_MASTER_TOOLTIP =
  "When on, pick at least one lifeline below (50:50, Double dip, Flip). Turning off all three turns this off. Turn off here to disable every lifeline for this lobby.";
const LIFELINE_TOOLTIP_50_50 =
  "50:50 — removes two incorrect answer choices, leaving the correct option and one wrong option.";
const LIFELINE_TOOLTIP_DOUBLE_DIP =
  "Double dip — player picks two answers then submits; if both are wrong, they get one more single pick.";
const LIFELINE_TOOLTIP_FLIP =
  "Flip — auto-generates 1 extra bank question (last row) when enabled; during play, a player may swap the current question with that reserve once.";

function normalizeBoolApi(v) {
  if (typeof v === "boolean") return v;
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return false;
}

function isFlipReserveBlank(q) {
  if (!q?.flipReserve) return false;
  const opts = Array.isArray(q.options) ? q.options : [];
  const allOptsEmpty = opts.every((o) => !String(o || "").trim());
  return !String(q.question || "").trim() && allOptsEmpty;
}

function isQuickValidMcq(q) {
  const opts = q?.options || [];
  const aid = Number(q?.answerId);
  if (!String(q?.question || "").trim()) return false;
  if (opts.length !== 4 || !opts.every((o) => String(o || "").trim())) return false;
  if (!Number.isFinite(aid) || aid < 1 || aid > 4) return false;
  return true;
}

/** True when Flip is on but the extra bank row is missing or not filled. */
function needsFlipBankGeneration(list, maxQ, lifelinesOn, flipOn) {
  if (!lifelinesOn || !flipOn) return false;
  const effective = maxQ + 1;
  const base = (Array.isArray(list) ? list : []).filter((q) => !isFlipReserveBlank(q));
  if (base.length < effective) return true;
  const assigned = assignFlipReserveToLastRow(base, lifelinesOn, flipOn, effective);
  if (assigned.length !== effective) return true;
  const reserve = assigned.find((q) => q.flipReserve);
  return !reserve || !isQuickValidMcq(reserve);
}

/** When Flip lifeline is on, exactly the last row is the bank reserve (main count = effectiveMax - 1). */
function assignFlipReserveToLastRow(list, lifelinesEnabled, lifelineFlip, effectiveMax) {
  const arr = Array.isArray(list) ? [...list] : [];
  const flipOn = lifelinesEnabled && lifelineFlip;
  if (!flipOn) return arr.map((q) => ({ ...q, flipReserve: false }));
  const em = Math.max(0, Number(effectiveMax) || 0);
  if (arr.length === em) {
    return arr.map((q, i) => ({ ...q, flipReserve: i === arr.length - 1 }));
  }
  /* List not yet full (or over): clear stale flip flags so we never require "exactly 1 reserve" on a bad row. */
  return arr.map((q) => ({ ...q, flipReserve: false }));
}

const MAX_QUESTION_CHARS = 100;
const MAX_OPTION_CHARS = 70;
const MAX_ANSWER_CHARS = 70;
const MAX_QUESTIONS_ALLOWED = 20;

function clampText(value, maxLen) {
  return String(value ?? "").slice(0, maxLen);
}

function seedFromString(str) {
  let h = 2166136261;
  const s = String(str ?? "");
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleWithSeed(arr, seedStr) {
  const a = Array.isArray(arr) ? [...arr] : [];
  const rand = mulberry32(seedFromString(seedStr));
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Randomize A–D order on the client; keeps the same correct answer text. */
function shuffleMcqOptionsFromAi(q, seed) {
  const aid = Number(q.answer_id);
  if (!(aid >= 1 && aid <= 4)) return q;
  const opts = [q.option_one, q.option_two, q.option_three, q.option_four].map((s) => String(s ?? "").trim());
  if (opts.some((x) => !x)) return q;
  const correctIdx = aid - 1;
  const indexed = opts.map((text, i) => ({ text, wasCorrect: i === correctIdx }));
  const shuffled = shuffleWithSeed(indexed, seed);
  const newAid = shuffled.findIndex((x) => x.wasCorrect) + 1;
  const newOpts = shuffled.map((x) => x.text);
  return {
    ...q,
    option_one: clampText(newOpts[0], MAX_OPTION_CHARS),
    option_two: clampText(newOpts[1], MAX_OPTION_CHARS),
    option_three: clampText(newOpts[2], MAX_OPTION_CHARS),
    option_four: clampText(newOpts[3], MAX_OPTION_CHARS),
    answer_id: newAid,
    correct_answer: clampText(newOpts[newAid - 1], MAX_ANSWER_CHARS),
  };
}

function validateQuestionLimits(list) {
  for (let i = 0; i < list.length; i += 1) {
    const q = list[i];
    const qText = String(q?.question ?? "").trim();
    if (qText.length > MAX_QUESTION_CHARS) {
      return `Question ${i + 1} exceeds ${MAX_QUESTION_CHARS} characters.`;
    }
    const opts = Array.isArray(q?.options) ? q.options : [];
    for (let j = 0; j < opts.length; j += 1) {
      if (String(opts[j] ?? "").trim().length > MAX_OPTION_CHARS) {
        return `Question ${i + 1}, option ${OPTION_LABELS[j]} exceeds ${MAX_OPTION_CHARS} characters.`;
      }
    }
  }
  return null;
}

function newQuestion(overrides = {}) {
  return {
    id: Date.now() + Math.random(),
    question: "",
    answer: "",
    answerId: 1,
    options: ["", "", "", ""],
    flipReserve: false,
    expanded: true,
    ...overrides,
  };
}

function parseAiResponse(responseText) {
  const clean = String(responseText || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

  // Fast path: well-formed JSON array (unchanged behavior for good responses).
  if (clean.startsWith("[") && clean.endsWith("]")) {
    try {
      const arr = JSON.parse(clean);
      if (Array.isArray(arr)) return arr;
    } catch {
      /* fall through to lenient recovery below */
    }
  }

  // Lenient recovery: the AI sometimes emits a single malformed object inside
  // an otherwise valid array (stray keys, missing quotes, truncation). Instead
  // of throwing away the whole batch, scan the text for balanced {...} blocks
  // and keep only the ones that parse into usable question objects.
  const recovered = extractJsonObjects(clean);
  if (recovered.length > 0) return recovered;

  throw new Error("AI returned invalid format.");
}

// Scans a string for top-level balanced { ... } blocks and returns the ones
// that JSON.parse successfully. Malformed/partial objects are skipped.
function extractJsonObjects(text) {
  const out = [];
  const s = String(text || "");
  let depth = 0;
  let start = -1;
  let inStr = false;
  let escaped = false;

  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];

    if (inStr) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === '"') {
        inStr = false;
      }
      continue;
    }

    if (ch === '"') {
      inStr = true;
    } else if (ch === "{") {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === "}") {
      if (depth > 0) {
        depth -= 1;
        if (depth === 0 && start >= 0) {
          const chunk = s.slice(start, i + 1);
          try {
            const obj = JSON.parse(chunk);
            if (obj && typeof obj === "object" && !Array.isArray(obj)) out.push(obj);
          } catch {
            /* skip malformed object */
          }
          start = -1;
        }
      }
    }
  }

  return out;
}

function buildPrompt({ topic, count, excludeQuestions = [] }) {
  const ex = Array.isArray(excludeQuestions)
    ? excludeQuestions.map((a) => String(a || "").trim()).filter(Boolean)
    : [];

  const excludeText = ex.length
    ? `

EXCLUDE LIST (do not repeat or closely paraphrase these question_name strings):
${ex.map((a) => `- ${a}`).join("\n")}
`
    : "";

  return `You are a strict JSON generator.

Return ONLY valid JSON.
No markdown.
No explanations.
No extra text.

Generate exactly ${count} multiple-choice quiz objects for the topic "${topic}".

Each object is ONE trivia question with FOUR answer choices. Exactly ONE choice is correct.

Each object must contain exactly these keys:
"question_name",
"option_one",
"option_two",
"option_three",
"option_four",
"answer_id"

RULES:

1. "question_name" must be a clear, grammatical trivia question (not a title like "Quiz 1"). It must relate to "${topic}" and be at most 100 characters.

2. "option_one" through "option_four" must be four DISTINCT, plausible strings (no duplicates), each at most 70 characters. Only one may be factually correct.

3. "answer_id" must be an INTEGER 1–4 for which option is correct (1=option_one, 2=option_two, 3=option_three, 4=option_four). Option order is shuffled in the app after generation.

4. Wrong options must be plausible distractors (same category or format as the correct one).

5. Do NOT include a separate "correct_answer" key in the JSON.

6. Generate ${count} different questions — no duplicate or near-duplicate "question_name" strings.

7. Vary difficulty and subtopics within "${topic}".

${excludeText}

Return ONLY the JSON array.`;
}

// ── Max questions per single AI call — never ask for more than this ───────────
const AI_BATCH_SIZE = 10;

function optionTextByAnswerId(q, aid) {
  const opts = [q?.option_one, q?.option_two, q?.option_three, q?.option_four];
  const i = Number(aid) - 1;
  if (i < 0 || i > 3) return "";
  return String(opts[i] ?? "").trim();
}

// ── Single AI call: fetch exactly `count` (capped at AI_BATCH_SIZE) questions ─
async function fetchAiBatch({ topic, count, excludeQuestions = [], rotationStartIndex = 0, token }) {
  const safeCount = Math.min(count, AI_BATCH_SIZE);

  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/generateAiQuestions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      prompt: buildPrompt({ topic, count: safeCount, excludeQuestions }),
    }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.message || "Failed to connect to AI service.");

  const parsed = parseAiResponse(payload?.response);
  const normalized = Array.isArray(parsed)
    ? parsed.map((q, idx) => {
        const rawAid = Number.parseInt(String(q?.answer_id ?? "").trim(), 10);
        const answer_id = Number.isFinite(rawAid) && rawAid >= 1 && rawAid <= 4 ? rawAid : 0;
        const o1 = clampText(String(q?.option_one || "").trim(), MAX_OPTION_CHARS);
        const o2 = clampText(String(q?.option_two || "").trim(), MAX_OPTION_CHARS);
        const o3 = clampText(String(q?.option_three || "").trim(), MAX_OPTION_CHARS);
        const o4 = clampText(String(q?.option_four || "").trim(), MAX_OPTION_CHARS);
        const question_name = clampText(String(q?.question_name || "").trim(), MAX_QUESTION_CHARS);
        const picked = optionTextByAnswerId(
          { option_one: o1, option_two: o2, option_three: o3, option_four: o4 },
          answer_id
        );
        return shuffleMcqOptionsFromAi(
          {
            question_name,
            option_one: o1,
            option_two: o2,
            option_three: o3,
            option_four: o4,
            answer_id,
            correct_answer: clampText(picked, MAX_ANSWER_CHARS),
          },
          `${topic}|${rotationStartIndex + idx}|${question_name}`
        );
      }).filter((q) => q.question_name && q.answer_id >= 1 && q.answer_id <= 4 && q.correct_answer)
    : [];

  const excludeSet = new Set(
    excludeQuestions.map((a) => String(a || "").trim().toLowerCase()).filter(Boolean)
  );
  const unique = [];
  for (const q of normalized) {
    const key = String(q.question_name || "").trim().toLowerCase();
    if (!key || excludeSet.has(key)) continue;
    excludeSet.add(key);
    unique.push(q);
  }

  return unique;
}

// ── Multi-batch fetcher: splits any count >10 into sequential batches of 10 ──
// If a batch comes back short (AI returned malformed/duplicate items that got
// dropped during parsing/normalization), we retry to top up to the requested
// count instead of giving up. A bounded number of unproductive attempts guards
// against an AI that keeps failing to produce usable questions.
async function fetchAiQuestions({ topic, count, excludeQuestions = [], rotationStartIndex = 0, token }) {
  const allResults = [];
  const runningExclude = [...excludeQuestions];
  let remaining = count;

  // Allow extra attempts beyond the ideal number of full batches so short
  // batches can be refilled. e.g. count=10 → up to a few retries to get all 10.
  const MAX_EMPTY_RETRIES = 3; // consecutive attempts that yield nothing → stop
  let emptyRetries = 0;

  while (remaining > 0) {
    const batchSize = Math.min(remaining, AI_BATCH_SIZE);
    let batch = [];
    try {
      batch = await fetchAiBatch({
        topic,
        count: batchSize,
        excludeQuestions: runningExclude,
        rotationStartIndex: rotationStartIndex + allResults.length,
        token,
      });
    } catch (e) {
      // Network/parse failure for this attempt — count it and retry up to the cap.
      emptyRetries += 1;
      if (emptyRetries >= MAX_EMPTY_RETRIES) {
        if (allResults.length > 0) break; // return whatever we gathered
        throw e; // nothing gathered at all → surface the error
      }
      continue;
    }

    if (batch.length === 0) {
      emptyRetries += 1;
      if (emptyRetries >= MAX_EMPTY_RETRIES) break;
      continue;
    }

    emptyRetries = 0; // made progress; reset the unproductive-attempt counter
    allResults.push(...batch);
    runningExclude.push(...batch.map((q) => String(q.question_name || "").trim()));
    remaining -= batch.length;
    // NOTE: a short batch (batch.length < batchSize) no longer breaks — the
    // loop continues and requests the still-missing `remaining` count.
  }

  return allResults;
}

function readLobbyNameFromSession() {
  try {
    const gs = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
    const ud = JSON.parse(sessionStorage.getItem("userData") || "{}");
    const l = gs?.lobby_name ?? ud?.lobby_name;
    if (l != null && String(l).trim() !== "") return String(l).trim();
  } catch {
    /* ignore */
  }
  return "";
}

function aiToQuestion(ai) {
  const answerId = Number(ai.answer_id) >= 1 && Number(ai.answer_id) <= 4 ? Number(ai.answer_id) : 1;
  const options = [
    clampText(String(ai.option_one ?? "").trim(), MAX_OPTION_CHARS),
    clampText(String(ai.option_two ?? "").trim(), MAX_OPTION_CHARS),
    clampText(String(ai.option_three ?? "").trim(), MAX_OPTION_CHARS),
    clampText(String(ai.option_four ?? "").trim(), MAX_OPTION_CHARS),
  ];
  return newQuestion({
    question: clampText(String(ai.question_name ?? "").trim(), MAX_QUESTION_CHARS),
    answer: clampText(String(ai.correct_answer || options[answerId - 1] || "").trim(), MAX_ANSWER_CHARS),
    answerId,
    options,
    expanded: true,
  });
}

function QuestionRow({ q, index, onToggle, onEdit, onChange, onDelete, isMobile, isEditing }) {
  const [local, setLocal] = useState(q);

  useEffect(() => {
    setLocal(q);
  }, [q]);

  const syncAnswerFromChoice = (st) => {
    const aid = Math.min(4, Math.max(1, Number(st.answerId) || 1));
    const opts = Array.isArray(st.options) ? st.options : [];
    const txt = clampText(String(opts[aid - 1] || "").trim(), MAX_ANSWER_CHARS);
    return { ...st, answerId: aid, answer: txt };
  };

  const update = (field, val) => {
    if (field === "answer") return;
    const capped = field === "question" ? clampText(val, MAX_QUESTION_CHARS) : val;
    const next = syncAnswerFromChoice({ ...local, [field]: capped });
    setLocal(next);
    onChange(next);
  };

  const updateOption = (i, val) => {
    const opts = [...local.options];
    opts[i] = clampText(val, MAX_OPTION_CHARS);
    const next = syncAnswerFromChoice({ ...local, options: opts });
    setLocal(next);
    onChange(next);
  };

  const setAnswerId = (aid) => {
    const next = syncAnswerFromChoice({ ...local, answerId: aid });
    setLocal(next);
    onChange(next);
  };

  const aid = Math.min(4, Math.max(1, Number(local.answerId) || 1));
  const correctLabel = OPTION_LABELS[aid - 1];
  const qPreview = String(local.question || "").trim() || `Question ${index + 1}`;
  const subText = local.flipReserve ? "Flip reserve (extra bank question)" : `MCQ · Correct: ${correctLabel}`;

  return (
    <div style={S.dropdownCard(isMobile)}>
      <div style={S.dropdownHeader(isMobile)} onClick={() => onToggle(q.id)}>
        <div style={S.qBadge(isMobile)}>Q{index + 1}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...S.qTitle(isMobile), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {qPreview}
          </div>
          <div style={S.qSub}>{subText}</div>
        </div>
        <div style={S.qActions} onClick={(e) => e.stopPropagation()}>
          <button style={S.iconBtn("#6b7db3", isEditing)} title="Edit" onClick={() => onEdit(q.id)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button style={S.iconBtn("#ef4444")} title="Delete" onClick={() => onDelete(q.id)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
          </button>
          <span style={{
            color: "#9aa5c4",
            fontSize: 18,
            lineHeight: 1,
            transform: q.expanded ? "rotate(90deg)" : "none",
            transition: "transform 0.2s",
          }}>›</span>
        </div>
      </div>

      {q.expanded && (
        <div style={isMobile ? S.expandBodyMobile : S.expandBody}>
          <input
            style={{ ...S.qInput(isMobile), opacity: isEditing ? 1 : 0.7, cursor: isEditing ? "text" : "not-allowed" }}
            value={local.question}
            placeholder={`Enter question (max ${MAX_QUESTION_CHARS} chars)…`}
            readOnly={!isEditing}
            maxLength={MAX_QUESTION_CHARS}
            onChange={(e) => update("question", e.target.value)}
          />
          <div style={S.optionsGrid(isMobile)}>
            {local.options.map((opt, i) => (
              <div key={i} style={S.optionRow}>
                <div style={S.optLabel}>{OPTION_LABELS[i]}</div>
                <input
                  style={{ ...S.optInput, opacity: isEditing ? 1 : 0.7, cursor: isEditing ? "text" : "not-allowed" }}
                  value={opt}
                  placeholder={`Option ${OPTION_LABELS[i]} (max ${MAX_OPTION_CHARS} chars)`}
                  readOnly={!isEditing}
                  maxLength={MAX_OPTION_CHARS}
                  onChange={(e) => updateOption(i, e.target.value)}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #edf0fa" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#1a2b5e", marginBottom: 8 }}>Correct answer</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {[1, 2, 3, 4].map((id) => (
                <label
                  key={id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: isEditing ? "pointer" : "not-allowed",
                    opacity: isEditing ? 1 : 0.65,
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#1a2b5e",
                  }}
                >
                  <input
                    type="radio"
                    name={`mcq-correct-${local.id}`}
                    checked={aid === id}
                    disabled={!isEditing}
                    onChange={() => setAnswerId(id)}
                  />
                  {OPTION_LABELS[id - 1]}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const isMobile = useIsMobile();

  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [shuffle, setShuffle] = useState(true);
  const [lifelinesEnabled, setLifelinesEnabled] = useState(false);
  const [fiftyFifty, setFiftyFifty] = useState(false);
  const [doubleDip, setDoubleDip] = useState(false);
  const [lifelineFlip, setLifelineFlip] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [hasHydratedQuestions, setHasHydratedQuestions] = useState(false);
  const [questionLimit, setQuestionLimit] = useState(10);
  const maxQuestions = Math.max(1, Math.min(MAX_QUESTIONS_ALLOWED, Number(questionLimit) || 10));
  const flipExtraSlots = lifelinesEnabled && lifelineFlip ? 1 : 0;
  const effectiveMaxQuestions = maxQuestions + flipExtraSlots;
  const limitReached = questions.length >= effectiveMaxQuestions;
  const [regenBaseCount, setRegenBaseCount] = useState(0);
  const [regenAfterDelete, setRegenAfterDelete] = useState(false);
  const [lobbyName, setLobbyName] = useState(readLobbyNameFromSession);
  const [themeName, setThemeName] = useState("guess_me");
  const dropRef = useRef(null);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/configure-game-settings"));
  }, [dispatch]);

  const isAnchorMode = useMemo(() => {
    if (location?.state?.isAnchorMode) return true;
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      return cached?.isAnchorMode === true;
    } catch {
      return false;
    }
  }, [location?.state?.isAnchorMode]);

  const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData]);

  useEffect(() => {
    document.documentElement.classList.add("manager-static-bg");
    document.body.classList.add("manager-static-bg");
    return () => {
      document.documentElement.classList.remove("manager-static-bg");
      document.body.classList.remove("manager-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!tokenForApi || !uid) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getManagerSettings({ token: tokenForApi, userId: uid, themeName });
        if (cancelled) return;
        const qn = resp?.questions_no != null ? Number(resp.questions_no) : NaN;
        if (Number.isFinite(qn) && qn > 0) setQuestionLimit(Math.min(MAX_QUESTIONS_ALLOWED, qn));
        if (resp?.lobby_name != null && String(resp.lobby_name).trim() !== "") {
          setLobbyName(String(resp.lobby_name).trim());
        } else {
          const fb = readLobbyNameFromSession();
          if (fb) setLobbyName(fb);
        }
        if (resp?.themename) setThemeName(String(resp.themename));
        if (resp?.shuffle_questions != null) setShuffle(normalizeBoolApi(resp.shuffle_questions));
        if (resp?.lifelines_enabled != null) setLifelinesEnabled(normalizeBoolApi(resp.lifelines_enabled));
        if (resp?.lifeline_fifty_fifty != null) setFiftyFifty(normalizeBoolApi(resp.lifeline_fifty_fifty));
        if (resp?.lifeline_double_dip != null) setDoubleDip(normalizeBoolApi(resp.lifeline_double_dip));
        if (resp?.lifeline_flip != null) setLifelineFlip(normalizeBoolApi(resp.lifeline_flip));
      } catch {
        if (!cancelled) {
          const fb = readLobbyNameFromSession();
          if (fb) setLobbyName((prev) => (prev && String(prev).trim() ? prev : fb));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tokenForApi, uid, themeName]);

  useEffect(() => {
    if (!tokenForApi || !uid || !lobbyName || hasHydratedQuestions) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getLobbyGameData({
          token: tokenForApi,
          userId: uid,
          themeName,
          lobby_name: lobbyName,
        });
        if (cancelled) return;
        if (resp?.shuffle_questions != null) setShuffle(normalizeBoolApi(resp.shuffle_questions));
        if (resp?.lifelines_enabled != null) setLifelinesEnabled(normalizeBoolApi(resp.lifelines_enabled));
        if (resp?.lifeline_fifty_fifty != null) setFiftyFifty(normalizeBoolApi(resp.lifeline_fifty_fifty));
        if (resp?.lifeline_double_dip != null) setDoubleDip(normalizeBoolApi(resp.lifeline_double_dip));
        if (resp?.lifeline_flip != null) setLifelineFlip(normalizeBoolApi(resp.lifeline_flip));

        const rows = Array.isArray(resp?.questions) ? resp.questions : [];
        if (rows.length > 0) {
          const flipOnHydrate =
            normalizeBoolApi(resp?.lifelines_enabled) && normalizeBoolApi(resp?.lifeline_flip);
          const maxQ = Math.max(1, Math.min(MAX_QUESTIONS_ALLOWED, Number(questionLimit) || 10));
          const effectiveHydrate = maxQ + (flipOnHydrate ? 1 : 0);
          const hydrated = rows.map((row, idx, arr) => {
            const options = [
              clampText(String(row?.option_one || "").trim(), MAX_OPTION_CHARS),
              clampText(String(row?.option_two || "").trim(), MAX_OPTION_CHARS),
              clampText(String(row?.option_three || "").trim(), MAX_OPTION_CHARS),
              clampText(String(row?.option_four || "").trim(), MAX_OPTION_CHARS),
            ];
            const rawAid = Number.parseInt(String(row?.answer_id ?? "").trim(), 10);
            const answerId =
              Number.isFinite(rawAid) && rawAid >= 1 && rawAid <= 4 ? rawAid : 1;
            const picked = clampText(String(options[answerId - 1] || "").trim(), MAX_ANSWER_CHARS);
            const stored = clampText(String(row?.correct_answer || "").trim(), MAX_ANSWER_CHARS);
            const markFlipReserve = flipOnHydrate && arr.length === effectiveHydrate && idx === arr.length - 1;
            return newQuestion({
              question: clampText(String(row?.question_name || "").trim(), MAX_QUESTION_CHARS),
              answer: picked || stored,
              answerId,
              options,
              expanded: false,
              ...(markFlipReserve ? { flipReserve: true } : {}),
            });
          });
          setQuestions(hydrated);
          setEditingQuestionId(null);
          setRegenBaseCount(hydrated.length);
          setRegenAfterDelete(false);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setHasHydratedQuestions(true);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenForApi, uid, lobbyName, themeName, hasHydratedQuestions, questionLimit]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (lifelinesEnabled) return;
    setFiftyFifty(false);
    setDoubleDip(false);
    setLifelineFlip(false);
    setQuestions((prev) => {
      let next = prev.filter((q) => !isFlipReserveBlank(q));
      if (next.length > maxQuestions) next = next.slice(0, maxQuestions);
      return next;
    });
  }, [lifelinesEnabled, maxQuestions]);

  /** Master stays on only while at least one sub-lifeline is on. */
  useEffect(() => {
    if (!lifelinesEnabled) return;
    if (!fiftyFifty && !doubleDip && !lifelineFlip) {
      setLifelinesEnabled(false);
    }
  }, [lifelinesEnabled, fiftyFifty, doubleDip, lifelineFlip]);

  const ensureFlipBankQuestion = useCallback(
    async (opts = {}) => {
      const flipOn = opts.flipOn ?? lifelineFlip;
      const lifelinesOn = opts.lifelinesOn ?? lifelinesEnabled;
      const topicTrim = String(opts.topic ?? topic).trim();
      const listSnapshot = Array.isArray(opts.questionsSnapshot) ? opts.questionsSnapshot : questions;

      if (!lifelinesOn || !flipOn) return false;
      if (!needsFlipBankGeneration(listSnapshot, maxQuestions, lifelinesOn, flipOn)) return true;

      if (!topicTrim) {
        await Swal.fire({
          icon: "info",
          title: "Topic required",
          text: "Enter a topic in Generate Questions, then enable Flip again to auto-create the flip-bank question.",
        });
        return false;
      }

      if (opts.manageLoading !== false) setLoading(true);
      Swal.fire({
        title: "Generating flip question…",
        html: "Creating <b>1</b> extra bank question for the Flip lifeline.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const effective = maxQuestions + 1;
        const excludeQuestions = listSnapshot.map((q) => String(q.question || "").trim()).filter(Boolean);
        const batch = await fetchAiQuestions({
          topic: topicTrim,
          count: 1,
          excludeQuestions,
          rotationStartIndex: listSnapshot.length,
          token: tokenForApi,
        });
        if (!batch.length) {
          throw new Error("Could not generate a flip-bank question. Try again.");
        }
        const generated = aiToQuestion(batch[0]);

        setQuestions((prev) => {
          let next = prev.filter((q) => !isFlipReserveBlank(q));
          if (next.length >= effective) next = next.slice(0, effective - 1);
          next = [...next, { ...generated, flipReserve: true, expanded: true }];
          return assignFlipReserveToLastRow(next, lifelinesOn, flipOn, effective);
        });

        Swal.close();
        await Swal.fire({
          icon: "success",
          title: "Flip question added",
          text: "Your flip-bank question is ready at the end of the list.",
          timer: 1800,
          showConfirmButton: false,
        });
        return true;
      } catch (e) {
        Swal.close();
        await Swal.fire("Error", e?.message || "Failed to generate flip question.", "error");
        return false;
      } finally {
        if (opts.manageLoading !== false) setLoading(false);
      }
    },
    [lifelineFlip, lifelinesEnabled, topic, questions, maxQuestions, tokenForApi]
  );

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    if (limitReached) return;

    // How many slots are left — guard BEFORE spinner starts
    const count = Math.max(0, effectiveMaxQuestions - questions.length);
    if (count <= 0) return;

    setLoading(true);
    setShowDropdown(false);

    Swal.fire({
      title: "Generating questions…",
      html: lifelinesEnabled && lifelineFlip
        ? `Creating up to <b>${count}</b> question(s), including the flip-bank slot if needed.`
        : `Creating up to <b>${count}</b> question(s).`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      setRegenBaseCount(effectiveMaxQuestions); // store TOTAL target for regen math
      setRegenAfterDelete(false);

      // Exclude question texts already in the list so AI never duplicates them
      const excludeQuestions = questions.map((q) => String(q.question || "").trim()).filter(Boolean);

      // fetchAiQuestions automatically splits into batches of 10
      // e.g. need 20 → calls AI twice: first 10, then 10 (excluding first batch questions)
      // e.g. need 7 → single call for 7
      const allGenerated = await fetchAiQuestions({
        topic,
        count,
        excludeQuestions,
        rotationStartIndex: questions.length,
        token: tokenForApi,
      });

      // Hard cap — should already be correct but belt-and-suspenders
      const finalBatch = allGenerated.slice(0, count);
      const generated = finalBatch.map(aiToQuestion);

      let combinedAfterGen = [];
      // APPEND to existing, hard cap at effectiveMaxQuestions
      setQuestions((prev) => {
        const combined = [...prev, ...generated].slice(0, effectiveMaxQuestions);
        combinedAfterGen = combined;
        return assignFlipReserveToLastRow(combined, lifelinesEnabled, lifelineFlip, effectiveMaxQuestions);
      });
      setAiResults(finalBatch);
      setRegenAfterDelete(false);
      setShowDropdown(false);

      Swal.close();

      if (
        lifelinesEnabled &&
        lifelineFlip &&
        needsFlipBankGeneration(combinedAfterGen, maxQuestions, lifelinesEnabled, lifelineFlip)
      ) {
        await ensureFlipBankQuestion({
          flipOn: true,
          lifelinesOn: true,
          questionsSnapshot: combinedAfterGen,
          manageLoading: false,
        });
      }
    } catch (e) {
      Swal.close();
      await Swal.fire("Error", e?.message || "Failed to generate questions.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateRemaining = async () => {
    const remainingCount = Math.max(0, (Number(regenBaseCount) || 0) - questions.length);
    if (!topic.trim()) return;
    if (remainingCount <= 0) return;
    if (limitReached) return;

    setLoading(true);
    setShowDropdown(false);

    try {
      const exclude = questions.map((q) => String(q.question || "").trim()).filter(Boolean);

      const batch = await fetchAiQuestions({
        topic,
        count: remainingCount,
        excludeQuestions: exclude,
        rotationStartIndex: questions.length,
        token: tokenForApi,
      });

      const generated = batch.map(aiToQuestion);

      setQuestions((prev) => {
        const room = Math.max(0, effectiveMaxQuestions - prev.length);
        if (room <= 0) return prev;
        const combined = [...prev, ...generated.slice(0, room)];
        return assignFlipReserveToLastRow(combined, lifelinesEnabled, lifelineFlip, effectiveMaxQuestions);
      });
      setAiResults((prev) => [...prev, ...batch]);
      setRegenAfterDelete(false);
      setShowDropdown(false);
    } catch (e) {
      await Swal.fire("Error", e?.message || "Failed to regenerate questions.", "error");
    } finally {
      setLoading(false);
    }
  };

  const addAiQuestion = (ai) => {
    if (limitReached) return;
    const q = aiToQuestion(ai);
    setQuestions((prev) => {
      if (prev.length >= effectiveMaxQuestions) return prev;
      const next = [...prev, q];
      return assignFlipReserveToLastRow(next, lifelinesEnabled, lifelineFlip, effectiveMaxQuestions);
    });
    setShowDropdown(false);
  };

  const addBlankQuestion = () =>
    setQuestions((prev) => {
      if (prev.length >= effectiveMaxQuestions) return prev;
      const isFlipSlot = lifelinesEnabled && lifelineFlip && prev.length === maxQuestions;
      const next = [...prev, newQuestion({ flipReserve: !!isFlipSlot, expanded: true })];
      return assignFlipReserveToLastRow(next, lifelinesEnabled, lifelineFlip, effectiveMaxQuestions);
    });

  const toggleExpand = (id) =>
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== id) return q;
        const nextExpanded = !q.expanded;
        if (!nextExpanded && editingQuestionId === id) setEditingQuestionId(null);
        return { ...q, expanded: nextExpanded };
      })
    );

  const editQuestion = (id) => {
    setEditingQuestionId(id);
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, expanded: true } : q)));
  };

  const updateQuestion = (updated) =>
    setQuestions((prev) => {
      const next = prev.map((q) => (q.id === updated.id ? { ...updated } : q));
      return assignFlipReserveToLastRow(next, lifelinesEnabled, lifelineFlip, effectiveMaxQuestions);
    });

  const deleteQuestion = (id) => {
    if (editingQuestionId === id) setEditingQuestionId(null);
    setRegenAfterDelete(true);
    setQuestions((prev) => {
      const next = prev.filter((q) => q.id !== id);
      return assignFlipReserveToLastRow(next, lifelinesEnabled, lifelineFlip, effectiveMaxQuestions);
    });
  };

  const canStartGame = useMemo(() => {
    const isValidMcq = (q) => {
      const opts = q.options || [];
      const aid = Number(q.answerId);
      if (!String(q.question || "").trim()) return false;
      if (opts.length !== 4 || !opts.every((o) => String(o || "").trim())) return false;
      if (!Number.isFinite(aid) || aid < 1 || aid > 4) return false;
      return true;
    };
    if (!questions.length) return false;
    if (validateQuestionLimits(questions)) return false;
    if (questions.some((q) => !isValidMcq(q))) return false;
    const flipOn = lifelinesEnabled && lifelineFlip;
    const requiredTotal = maxQuestions + (flipOn ? 1 : 0);
    if (flipOn) {
      if (questions.length !== requiredTotal) return false;
      return questions.filter((q) => q.flipReserve).length === 1;
    }
    return questions.length > 0 && questions.length <= maxQuestions;
  }, [questions, lifelinesEnabled, lifelineFlip, maxQuestions]);

  const handleStartGame = async () => {
    if (!tokenForApi || !uid || !lobbyName) {
      Swal.fire("Error", "Missing token/user/lobby. Please go back and configure the lobby first.", "error");
      return;
    }

    const limitErr = validateQuestionLimits(questions);
    if (limitErr) {
      await Swal.fire("Error", limitErr, "error");
      return;
    }

    const isValidMcq = (q) => {
      const opts = q.options || [];
      const aid = Number(q.answerId);
      if (!String(q.question || "").trim()) return false;
      if (opts.length !== 4 || !opts.every((o) => String(o || "").trim())) return false;
      if (!Number.isFinite(aid) || aid < 1 || aid > 4) return false;
      return true;
    };

    const flipOn = lifelinesEnabled && lifelineFlip;
    const requiredTotal = maxQuestions + (flipOn ? 1 : 0);

    if (flipOn) {
      if (questions.length !== requiredTotal) {
        await Swal.fire(
          "Error",
          `Flip is ON. Generate and fill the extra flip-bank question (last row) so you have ${requiredTotal} total questions, then Start Game will unlock.`,
          "error"
        );
        return;
      }
      const reserves = questions.filter((q) => q.flipReserve);
      if (reserves.length !== 1) {
        await Swal.fire(
          "Error",
          "Flip is ON — the last row must be the flip-bank question. Fill that final row to unlock Start Game.",
          "error"
        );
        return;
      }
    } else if (questions.length > maxQuestions) {
      await Swal.fire("Error", `At most ${maxQuestions} questions when Flip is off.`, "error");
      return;
    }

    const invalid = questions.filter((q) => !isValidMcq(q));
    if (invalid.length) {
      await Swal.fire(
        "Error",
        "Every question needs a prompt, four filled answer options, and a correct answer (A–D).",
        "error"
      );
      return;
    }

    const payloadQuestions = questions.map((q) => {
      const aid = Math.min(4, Math.max(1, Number(q.answerId) || 1));
      const correctText = clampText(String(q.options?.[aid - 1] || "").trim(), MAX_ANSWER_CHARS);
      return {
        question_name: clampText(String(q.question || "").trim(), MAX_QUESTION_CHARS),
        option_one: clampText(String(q.options?.[0] || "").trim(), MAX_OPTION_CHARS),
        option_two: clampText(String(q.options?.[1] || "").trim(), MAX_OPTION_CHARS),
        option_three: clampText(String(q.options?.[2] || "").trim(), MAX_OPTION_CHARS),
        option_four: clampText(String(q.options?.[3] || "").trim(), MAX_OPTION_CHARS),
        correct_answer: correctText,
        answer_id: String(aid),
        is_active: "true",
      };
    });

    if (!payloadQuestions.length) {
      Swal.fire("Error", "No questions to save. Please add at least one question.", "error");
      return;
    }

    try {
      const isGuest = deriveGuestFromUser(storedUserData);
      const gameId = String(storedUserData.gameId ?? "").trim();
      const sessionId = String(storedUserData.sessionId ?? "").trim();
      const organizationId = String(storedUserData.organizationId ?? "").trim();

      let joinLink = "";
      if (gameId && sessionId && organizationId) {
        const base = buildExtramileJoinUrl(isGuest, gameId, sessionId, organizationId);
        joinLink = withQuestionAmp(appendLobbyShareFlagsToJoinUrl(base, lobbyName));
      } else {
        joinLink = buildFallbackAppLink(lobbyName);
      }

      try {
        await updateGameSettings({
          token: tokenForApi,
          userId: uid,
          themeName,
          current_mode: "Manager",
          questions_no: String(
            flipOn ? questions.filter((q) => !q.flipReserve).length : questions.length
          ),
          shuffle_questions: shuffle,
          lifelines_enabled: lifelinesEnabled,
          lifeline_fifty_fifty: lifelinesEnabled && fiftyFifty,
          lifeline_double_dip: lifelinesEnabled && doubleDip,
          lifeline_flip: lifelinesEnabled && lifelineFlip,
          lobby_link: joinLink || null,
          email: storedUserData?.email ?? null,
          fullName: storedUserData?.name ?? storedUserData?.fullName ?? null,
          employeeId: storedUserData?.employeeId ?? null,
          reportid: storedUserData?.reportId ?? null,
        });
      } catch {
        // ignore
      }

      const res = await addManagerQuestions({
        token: tokenForApi,
        userId: uid,
        themename: themeName,
        lobby_name: lobbyName,
        questions: payloadQuestions,
      });

      await Swal.fire("Saved!", `Questions saved: ${res?.inserted ?? payloadQuestions.length}`, "success");

      if (isAnchorMode) {
        navigate("/quiz-ui", {
          replace: true,
          state: { lobby_name: lobbyName, isAnchorMode: true, backTo: "/manager-dashboard" },
        });
      } else {
        navigate("/usergame", { replace: true, state: { lobby_name: lobbyName } });
      }
    } catch (e) {
      await Swal.fire("Error", e?.message || "Failed to save questions.", "error");
    }
  };

  const handleToggleLifelineFlip = useCallback(
    async (e) => {
      e?.stopPropagation?.();
      if (!lifelinesEnabled || loading) return;

      if (lifelineFlip) {
        setLifelineFlip(false);
        setQuestions((qList) => {
          let next = qList.filter((q) => !isFlipReserveBlank(q));
          if (next.length > maxQuestions) next = next.slice(0, maxQuestions);
          return assignFlipReserveToLastRow(next, lifelinesEnabled, false, maxQuestions);
        });
        return;
      }

      const snapshot = questions;
      setLifelineFlip(true);
      await ensureFlipBankQuestion({
        flipOn: true,
        lifelinesOn: true,
        questionsSnapshot: snapshot,
      });
    },
    [lifelinesEnabled, lifelineFlip, loading, questions, maxQuestions, ensureFlipBankQuestion]
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Nunito', sans-serif; }
        input:focus { border-color: #4f9ef8 !important; box-shadow: 0 0 0 3px rgba(79,158,248,0.15); }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-thumb { background: #d4dcf8; border-radius: 6px; }
      `}</style>

      <div style={S.page(isMobile)}>
        <div style={S.bgBlob1} />
        <div style={S.bgBlob2} />
        {!isMobile && (
          <div style={S.qmarks}>
            {["?", "?", "?", "?"].map((c, i) => (
              <span key={i} style={S.qmark}>{c}</span>
            ))}
          </div>
        )}

        <div style={S.inner}>
          <h1 style={S.title(isMobile)}>Manager Dashboard</h1>

          {/* Top row: Generate + Settings cards */}
          <div style={S.topRow(isMobile)}>
            <div style={{ ...S.card(isMobile), ...S.genCard(isMobile) }}>
              <div style={S.cardTitle(isMobile)}>Generate Questions</div>
              <div style={{ position: "relative" }} ref={dropRef}>
                <input
                  style={S.topicInput(isMobile)}
                  value={topic}
                  placeholder="Enter topic (e.g. Animals, Movies…)"
                  onChange={(e) => setTopic(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                />
                {showDropdown && aiResults.length > 0 && (
                  <div style={S.aiDropdown}>
                    {aiResults.map((ai, i) => (
                      <div
                        key={i}
                        style={S.aiItem}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#eef2ff")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                        onClick={() => addAiQuestion(ai)}
                      >
                        <div style={{ fontSize: 13, color: "#2563eb", marginBottom: 2, fontWeight: 700 }}>
                          Correct: {OPTION_LABELS[Math.min(4, Math.max(1, Number(ai.answer_id) || 1)) - 1]}
                        </div>
                        <div style={{ fontSize: 13, color: "#4b5e8a", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {ai.question_name}
                        </div>
                        <div style={{ fontSize: 11, color: "#9aa5c4", marginTop: 3 }}>
                          {[ai.option_one, ai.option_two, ai.option_three, ai.option_four].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                style={{ ...S.genBtn(isMobile), opacity: loading || limitReached ? 0.6 : 1, cursor: loading || limitReached ? "not-allowed" : "pointer" }}
                onClick={handleGenerate}
                disabled={loading || limitReached}
              >
                {loading ? (
                  <><span style={S.spinner} /> Generating…</>
                ) : (
                  <><span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Generate</>
                )}
              </button>
            </div>

            <div style={{ ...S.card(isMobile), ...S.settCard(isMobile) }}>
              <div style={S.cardTitle(isMobile)}>Settings</div>
              <div style={S.checkRow} onClick={() => setShuffle((v) => !v)}>
                <div style={S.checkBox(shuffle)}>
                  {shuffle && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span style={S.checkLabel}>Shuffle Questions</span>
              </div>
              <div
                style={{ ...S.checkRow, marginTop: 12 }}
                onClick={() => {
                  if (lifelinesEnabled) {
                    setLifelinesEnabled(false);
                  } else {
                    setLifelinesEnabled(true);
                    setFiftyFifty(true);
                  }
                }}
                title={LIFELINES_MASTER_TOOLTIP}
              >
                <div style={S.checkBox(lifelinesEnabled)}>
                  {lifelinesEnabled && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span style={S.checkLabel}>Lifelines</span>
              </div>
              <div style={{ marginLeft: 32, marginTop: 10, display: "flex", flexDirection: "column", gap: 8, opacity: lifelinesEnabled ? 1 : 0.45, pointerEvents: lifelinesEnabled ? "auto" : "none" }}>
                <div
                  style={S.checkRow}
                  title={LIFELINE_TOOLTIP_50_50}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!lifelinesEnabled) return;
                    setFiftyFifty((v) => !v);
                  }}
                >
                  <div style={S.checkBox(fiftyFifty)}>
                    {fiftyFifty && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <span style={{ ...S.checkLabel, fontSize: 15 }}>50:50</span>
                </div>
                <div
                  style={S.checkRow}
                  title={LIFELINE_TOOLTIP_DOUBLE_DIP}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!lifelinesEnabled) return;
                    setDoubleDip((v) => !v);
                  }}
                >
                  <div style={S.checkBox(doubleDip)}>
                    {doubleDip && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <span style={{ ...S.checkLabel, fontSize: 15 }}>Double dip</span>
                </div>
                <div
                  style={S.checkRow}
                  title={LIFELINE_TOOLTIP_FLIP}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!lifelinesEnabled) return;
                    void handleToggleLifelineFlip(e);
                  }}
                >
                  <div style={S.checkBox(lifelineFlip)}>
                    {lifelineFlip && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <span style={{ ...S.checkLabel, fontSize: 15 }}>Flip</span>
                </div>
              </div>
            </div>
          </div>

          {/* Questions header */}
          <div style={S.sectionLabel(isMobile)}>Questions ({questions.length}/{effectiveMaxQuestions})</div>

          {/* Regenerate remaining button — only shown after a manual delete */}
          {regenAfterDelete && Math.max(0, (Number(regenBaseCount) || 0) - questions.length) > 0 && (
            <div style={{ marginBottom: 14 }}>
              <button
                type="button"
                style={{ ...S.genBtn(isMobile), maxWidth: 360, marginInline: "auto", opacity: loading ? 0.75 : 1 }}
                onClick={handleRegenerateRemaining}
                disabled={loading || !topic.trim() || limitReached}
              >
                {loading ? (
                  <><span style={S.spinner} /> Regenerating…</>
                ) : (
                  <>Regenerate remaining {Math.max(0, (Number(regenBaseCount) || 0) - questions.length)}</>
                )}
              </button>
            </div>
          )}

          {/* Question list */}
          {questions.map((q, i) => (
            <QuestionRow
              key={q.id}
              q={q}
              index={i}
              isMobile={isMobile}
              onToggle={toggleExpand}
              onEdit={editQuestion}
              onChange={updateQuestion}
              onDelete={deleteQuestion}
              isEditing={editingQuestionId === q.id}
            />
          ))}

          <button
            style={S.addNewBtn(isMobile)}
            onClick={addBlankQuestion}
            disabled={limitReached}
            onMouseEnter={(e) => {
              if (limitReached) return;
              e.currentTarget.style.background = "rgba(255,255,255,0.85)";
              e.currentTarget.style.borderColor = "#4f9ef8";
              e.currentTarget.style.color = "#2563eb";
            }}
            onMouseLeave={(e) => {
              if (limitReached) return;
              e.currentTarget.style.background = "rgba(255,255,255,0.5)";
              e.currentTarget.style.borderColor = "#b8c8f0";
              e.currentTarget.style.color = "#6b7db3";
            }}
          >
            {limitReached ? "Question limit reached" : "+ Add New Question"}
          </button>

          <button
            style={{ ...S.startBtn(isMobile), opacity: canStartGame && tokenForApi && uid && lobbyName ? 1 : 0.55 }}
            onClick={handleStartGame}
            disabled={!canStartGame || !tokenForApi || !uid || !lobbyName}
            type="button"
          >
            Start Game
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </button>
        </div>

        <button
          style={{ ...S.fab, opacity: limitReached ? 0.5 : 1, cursor: limitReached ? "not-allowed" : "pointer" }}
          onClick={addBlankQuestion}
          title={limitReached ? "Question limit reached" : "Add Question"}
          disabled={limitReached}
        >
          +
        </button>
      </div>
    </>
  );
}
